import { z } from "zod";
import { BaseDrawer, BaseDrawerState, CreateBaseDrawerHook } from "../base_drawer";
import { DefaultFormNewSwitch } from "../../default_form_new_switch";
import { DefaultFormAutocomplete } from "../../default_form_autocomplete";
import { CentroCustoDropDown } from "../../../domain/models/centro_custo_dropdown";
import { FormCNPJMaskedInput } from "../../masked_inputs/react_forms/form_cnpj_masked_input";
import { FormCPFMaskedInput } from "../../masked_inputs/react_forms/form_cpf_masked_input";

import DefaultFormInput from "../../default_form_input";
import { FormSwitchInput } from "../../masked_inputs/react_forms/form_switch_input";

/**
 * Schema
 */

const FornecedorSchema = z.object({
  ativo: z.coerce.number().default(1),
  avaliarFornecedor: z.coerce.number().default(0),
  fornecedorProdutos: z.coerce.number().default(0),
  fornecedorServicos: z.coerce.number().default(0),
  nomeFantasia: z.string({ required_error: 'Este campo deve ser preenchido!' }).nonempty({ message: 'Este campo deve ser preenchido!' }),
  razaoSocial: z.string().nullable().default(null),
  centroCusto: z.coerce.number().nullable().default(null),
  cnpj: z.string().nullable().default(null),
  cpf: z.string().nullable().default(null),
  observacoes: z.string().nullable().default(null),
  outro: z.coerce.number().default(1),
});

type FornecedorForm = z.infer<typeof FornecedorSchema>;

/**
 * defaultFornecedorForm
 */

const defaultFornecedorForm: FornecedorForm = {
  ativo: 1,
  avaliarFornecedor: 1,
  fornecedorProdutos: 1,
  fornecedorServicos: 0,
  nomeFantasia: '',
  razaoSocial: '',
  centroCusto: null,
  cnpj: null,
  cpf: null,
  observacoes: null,
  outro: 1,
}

/**
 * useFornecedorDrawer
 * [fornecedorStateDrawer, startFornecedorDrawer]
 */

export const useFornecedorDrawer = CreateBaseDrawerHook<FornecedorForm>();

/**
 * FornecedorDrawer
 */

type Props = {
  state: BaseDrawerState<FornecedorForm>;
  saving: boolean;
  centrosCustoDropdown: CentroCustoDropDown[];
};

export const FornecedorDrawer = ({
  state,
  saving,
  centrosCustoDropdown,
}: Props) => {
  return (
    <BaseDrawer
      title="Novo Fornecedor"
      saving={saving}
      state={state}
      schema={FornecedorSchema}
      defaultValues={defaultFornecedorForm}>
      {
        (hookForm) => (
          <>
            <DefaultFormNewSwitch
              name="ativo"
              label="Ativo"
              arrangement="horizontal"
              reactHookForm={hookForm}
            />

            <DefaultFormNewSwitch
              name="avaliarFornecedor"
              label="Avaliar Fornecedor?"
              arrangement="horizontal"
              reactHookForm={hookForm}
            />

            <DefaultFormNewSwitch
              name="fornecedorProdutos"
              label="Fornecedor de Produtos?"
              arrangement="horizontal"
              reactHookForm={hookForm}
            />

            <DefaultFormNewSwitch
              name="fornecedorServicos"
              label="Fornecedor de Serviços?"
              arrangement="horizontal"
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name="nomeFantasia"
              label="Nome Fantasia"
              required={true}
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name="razaoSocial"
              label="Razão Social"
              reactHookForm={hookForm}
            />

            <DefaultFormAutocomplete
              name="centroCusto"
              label="Centro de Custo"
              fullWidth={true}
              options={centrosCustoDropdown}
              getOptionKey={x => x.id}
              getOptionLabel={x => x.title}
              reactHookForm={hookForm}
            />

            <FormCNPJMaskedInput
              name="cnpj"
              label="CNPJ"
              reactHookForm={hookForm}
            />

            <FormCPFMaskedInput
              name="cpf"
              label="CPF"
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name="observacoes"
              label="Observações"
              multiline={true}
              rows={4}
              reactHookForm={hookForm}
            />

            <div className="w-full py-2 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between">
                <label className="font-medium text-gray-700">Qualificação:</label>
                <FormSwitchInput
                  name='outro'
                  activeLabel="Outros"
                  inactiveLabel="Não"
                  readOnly={true}
                  reactHookForm={hookForm}
                />
              </div>
            </div>

            <div className="w-full py-2 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-red-700">
                  Assim que possível, atualize a qualificação do fornecedor!
                </span>
              </div>
            </div>
          </>
        )
      }
    </BaseDrawer>
  );
}